import { useGridUserDataGlobalFiltersContext } from "contexts/GridUserDataGlobalFiltersProvider";
import { useTerminalFieldVisibility } from "hooks/useTerminalFieldVisibility";
import React, { useState } from "react";
import { useDataProvider, usePermissions, useTranslate } from "react-admin";

import { OrderNormalStatuses } from "../../../entities";
import { OrderStatus } from "../../../entities/OrderStatus";
import { AllCustomerRoles } from "../../../entities/UserRole";
import { useExportColumns } from "../../../hooks/useExportColumns";
import { useExportColumnsSelection } from "../../../hooks/useExportColumnsSelection";
import { formatISOWithoutTimeZone } from "../../_helpers/dateTime";
import { CompanyFilterField } from "../ExportDialog/CompanyFilterField";
import { CopyCSVButton } from "../ExportDialog/CopyCSVButton";
import { DateFilterField } from "../ExportDialog/DateFilterField";
import { ExportColumnsButton } from "../ExportDialog/ExportColumnsButton";
import { ExportModalBase } from "../ExportDialog/ExportModalBase";
import { ExportTriggerButton } from "../ExportDialog/ExportTriggerButton";
import { StatusFilterField } from "../ExportDialog/StatusFilterField";
import { useSimpleModal } from "../useSimpleModal";

const getKeyByStatus = (status) => {
	if (status === OrderStatus.ORDER_COMPLETED) {
		return "completed_at";
	}
	return "created_at";
};

const titleByKey = {
	completed_at: "COMPL Date",
	created_at: "REG Date",
};

const ORDERS_EXPORT_HEADER_ENDPOINT = "orders-export-header";

const uniqueColumns = (...columnLists) => [...new Set(columnLists.flat())];

const getIsTerminalDataReady = (terminalId, currentTerminal) => !terminalId || currentTerminal !== undefined;

const OrdersExportWithFiltersView = ({
	filters,
	columns,
	columnsChoices,
	hiddenFields,
	onModalClose,
}) => {
	const { columnsSelected, setColumnsSelected, excludedColumns } =
		useExportColumnsSelection(columns);

	const filtersWithExcluded = React.useMemo(
		() => ({
			...filters,
			excluded_columns: uniqueColumns(
				filters?.excluded_columns ?? [],
				hiddenFields,
				excludedColumns
			),
		}),
		[filters, hiddenFields, excludedColumns]
	);

	const titleButtons = [
		<ExportColumnsButton
			choices={columnsChoices}
			value={columnsSelected}
			onChange={setColumnsSelected}
		/>,
	];

	return (
		<ExportModalBase
			filters={filtersWithExcluded}
			endpoint="orders"
			titleButtons={titleButtons}
			onModalClose={onModalClose}
		/>
	);
};

export const OrdersExportWithFilters = ({ filters }) => {
	const exportModal = useSimpleModal();
	const { data: globalFilter = {} } = useGridUserDataGlobalFiltersContext();
	const terminalId = globalFilter?.terminal_id?.[0];
	const { hiddenFields = [], currentTerminal } = useTerminalFieldVisibility(terminalId);
	const { isLoading, columns, columnsChoices, loadColumns } = useExportColumns(
		ORDERS_EXPORT_HEADER_ENDPOINT,
		{ hiddenFields }
	);
	const isTerminalDataReady = getIsTerminalDataReady(terminalId, currentTerminal);

	const onOpen = async () => {
		await loadColumns({ company_id: filters?.company_id ?? undefined });
		exportModal.handleOpen();
	};

	return (
		<React.Fragment>
			<ExportTriggerButton
				label="resources.orders.export"
				isLoading={isLoading}
				disabled={!isTerminalDataReady}
				onClick={onOpen}
			/>
			{exportModal.opened && (
				<OrdersExportWithFiltersView
					filters={filters}
					columns={columns}
					columnsChoices={columnsChoices}
					hiddenFields={hiddenFields}
					onModalClose={exportModal.handleClose}
				/>
			)}
		</React.Fragment>
	);
};

export const OrdersExportView = ({
	userId,
	viewComponentProps,
	initDateFrom,
	columnsChoices,
	columns,
	setHeaderFilter,
	hiddenFields,
	ViewComponent = ExportModalBase,
}) => {
	const tableRef = React.useRef(null);
	const translate = useTranslate();
	const { permissions } = usePermissions();
	const hideCompanyFilter = permissions && AllCustomerRoles.includes(permissions.role);

	const [exportDate, setExportDate] = React.useState(null);
	const [dateFromFilter, setDateFromFilter] = React.useState(initDateFrom);
	const [dateToFilter, setDateToFilter] = React.useState(null);
	const [statusId, setStatusId] = React.useState(undefined);
	const [companyId, setCompanyId] = React.useState(undefined);
	const { columnsSelected, setColumnsSelected, excludedColumns } =
		useExportColumnsSelection(columns);

	const endpoint = "orders";
	const filterKey = getKeyByStatus(statusId);

	const filters = React.useMemo(() => {
		// Info: we nned t oconvert to str without time zone here because
		const strDateFromFilter = formatISOWithoutTimeZone(dateFromFilter);
		const strDateToFilter = formatISOWithoutTimeZone(dateToFilter);
		return {
			[`${filterKey}:gt`]: strDateFromFilter,
			[`${filterKey}:lt`]: strDateToFilter,
			...(statusId ? { status: [statusId] } : {}),
			company_id: companyId,
			excluded_columns: uniqueColumns(hiddenFields, excludedColumns),
		};
	}, [dateFromFilter, dateToFilter, statusId, companyId, filterKey, excludedColumns, hiddenFields]);

	React.useEffect(() => {
		setHeaderFilter({
			company_id: companyId,
		});
	}, [companyId]);

	const titleButtons = [
		<StatusFilterField
			resource="order-statuses"
			value={statusId}
			onChange={setStatusId}
			label={translate("resources.orders.export-dialog.fields.status")}
			filter={{ id: OrderNormalStatuses }}
		/>,
		!hideCompanyFilter && (
			<CompanyFilterField
				value={companyId}
				onChange={setCompanyId}
				label={translate("resources.orders.export-dialog.fields.company_id")}
			/>
		),
		<DateFilterField
			label={translate("resources.orders.export-dialog.fields.date_from")}
			onChange={setDateFromFilter}
			value={dateFromFilter}
		/>,
		<DateFilterField
			label={translate("resources.orders.export-dialog.fields.date_to")}
			onChange={setDateToFilter}
			value={dateToFilter}
		/>,
		<ExportColumnsButton
			choices={columnsChoices}
			value={columnsSelected}
			onChange={setColumnsSelected}
		/>,
		<CopyCSVButton
			table={tableRef}
			userId={userId}
			exportDate={exportDate}
			setDateFromFilter={setDateFromFilter}
		/>,
	];

	return (
		<ViewComponent
			filters={filters}
			endpoint={endpoint}
			tableRef={tableRef}
			{...viewComponentProps}
			titleButtons={titleButtons}
			setExportDate={setExportDate}
		/>
	);
};

export const OrdersExportModal = ({ userId }) => {
	const exportModal = useSimpleModal();
	const dataProvider = useDataProvider();
	const [initDateFrom, setInitDateFrom] = useState(new Date(Date.now()));
	const [headerFilter, setHeaderFilter] = useState({});
	const { data: globalFilter = {} } = useGridUserDataGlobalFiltersContext();
	const terminalId = globalFilter?.terminal_id?.[0];
	const { hiddenFields = [], currentTerminal } = useTerminalFieldVisibility(terminalId);
	const { isLoading, columns, columnsChoices, loadColumns } = useExportColumns(
		ORDERS_EXPORT_HEADER_ENDPOINT,
		{ hiddenFields }
	);
	const isTerminalDataReady = getIsTerminalDataReady(terminalId, currentTerminal);

	const onOpen = async () => {
		const promLastDateRes = await dataProvider.getOne("orders/last-export-date", {
			id: userId,
		});

		if (promLastDateRes.data?.date) {
			const _initDateFrom = new Date(promLastDateRes.data.date);
			if (_initDateFrom) setInitDateFrom(_initDateFrom);
		}

		setHeaderFilter({});

		await loadColumns({});

		exportModal.handleOpen();
	};

	React.useEffect(() => {
		if (exportModal.opened && isTerminalDataReady) {
			loadColumns(headerFilter);
		}
	}, [headerFilter, isTerminalDataReady]);

	return (
		<>
			<ExportTriggerButton label="osfc.buttons.export_search" isLoading={isLoading} onClick={onOpen} />

			{exportModal.opened && (
				<OrdersExportView
					userId={userId}
					viewComponentProps={{ onModalClose: exportModal.handleClose }}
					initDateFrom={initDateFrom}
					columns={columns}
					setHeaderFilter={setHeaderFilter}
					columnsChoices={columnsChoices}
					hiddenFields={hiddenFields}
				/>
			)}
		</>
	);
};
